import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/** Merge conditional classes, with later Tailwind utilities winning over earlier ones. */
export const cn = (...inputs) => twMerge(clsx(inputs));

/**
 * **The id behind a reference, whether or not the API populated it.**
 *
 * A ref comes back in three shapes and a screen cannot tell which it will get:
 *
 *   - a bare id string, when nothing populated it;
 *   - `{ id, … }` on the *top-level* document, because `paginate()` normalises that one;
 *   - `{ _id, … }` on a **populated sub-document**, because `normaliseId` only touches the
 *     top level and there is no lean-virtuals plugin in this app — so a populated party has
 *     `_id` and no `id` at all.
 *
 * That third case is the one that bites, and it bites silently: `String(ref?.id ?? ref)`
 * looks defensive, falls through to the object, and yields `"[object Object]"`. Compared
 * against a real id it simply never matches, so a filter built on it returns an empty list
 * and the screen looks like it has no data rather than like it has a bug. That is exactly
 * how the owner's property picker came back empty.
 *
 * @returns {string|null} the id as a string, or `null` when there is no reference at all
 */
export const idOf = (ref) => {
  if (!ref) return null;
  if (typeof ref === 'string') return ref;
  return ref.id ? String(ref.id) : ref._id ? String(ref._id) : null;
};

/** True when two references point at the same record, in any of the shapes above. */
export const sameRef = (a, b) => {
  const left = idOf(a);
  return !!left && left === idOf(b);
};
